import _ from 'lodash';
import moment from 'moment';
import { toCents } from '@washday/utilities';
import OrderStatuses from '../enum/OrderStatuses';
import { DEFAULT_FOLIO_PREFIX } from '../constants';
import { IOrderPaymentLines } from '../interfaces/Order';
import { IOrderProduct } from '../interfaces/Product';

export const isTimeFormat = (time: string): boolean => {
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
};

export const normalizeOrderScheduleTimeValue = (value?: string): string => {
    if (!value) return '';
    if (isTimeFormat(value)) return value;

    const parsedIsoValue = moment.parseZone(value, moment.ISO_8601, true);
    if (parsedIsoValue.isValid()) {
        return parsedIsoValue.format('HH:mm');
    }

    const parsedValue = moment(value);
    return parsedValue.isValid() ? parsedValue.format('HH:mm') : '';
};

export const differencesKeys = (obj1, obj2) => {
    const compareObjects = (obj1, obj2) => {
        const result = {};

        const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

        keys.forEach(key => {
            if (!_.isEqual(obj1[key], obj2[key])) {
                if (_.isObject(obj1[key]) && _.isObject(obj2[key])) {
                    if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
                        result[key] = compareArrays(obj1[key], obj2[key]);
                    } else {
                        result[key] = compareObjects(obj1[key], obj2[key]);
                    }
                } else {
                    result[key] = { obj1: obj1[key], obj2: obj2[key] };
                }
            }
        });

        return result;
    };

    const compareArrays = (arr1, arr2) => {
        const result = [];
        const maxLength = Math.max(arr1.length, arr2.length);

        for (let i = 0; i < maxLength; i++) {
            if (!_.isEqual(arr1[i], arr2[i])) {
                if (_.isObject(arr1[i]) && _.isObject(arr2[i])) {
                    result[i] = compareObjects(arr1[i], arr2[i]);
                } else {
                    result[i] = { arr1: arr1[i], arr2: arr2[i] };
                }
            }
        }

        return result;
    };

    const differences = compareObjects(obj1, obj2);

    const getDifferenceKeys = (obj, parentKey = '') => {
        let keys = [];

        Object.keys(obj).forEach(key => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (_.isObject(obj[key]) && !Array.isArray(obj[key])) {
                keys = keys.concat(getDifferenceKeys(obj[key], fullKey));
            } else if (Array.isArray(obj[key])) {
                obj[key].forEach((item, index) => {
                    if (item !== undefined) {
                        keys = keys.concat(getDifferenceKeys(item, `${fullKey}.${index}`));
                    }
                });
            } else {
                keys.push(fullKey);
            }
        });

        return keys;
    };

    return getDifferenceKeys(differences);
};

export const getOrderCardColorsByAmountDueAndStatus = ({
    amountDue,
    highlightOrderCardsByPaymentPending,
    status
}: {
    amountDue?: number,
    highlightOrderCardsByPaymentPending?: boolean,
    status?: string
}): { headerBackgroundColor?: string; headerTextColor?: string } => {

    // 1) Uncollected (nuevo color morado)
    if (status === OrderStatuses.UNCOLLECTED) {
        return {
            headerBackgroundColor: '#7E57C2', // purple
            headerTextColor: 'white'
        };
    }

    if (!highlightOrderCardsByPaymentPending) return {};

    // Normalize to cents to avoid floating point drift (e.g. 606.08 - 606.0799999999999)
    const amountDueCents = toCents(amountDue || 0);

    // 2) Payment pending
    if (amountDueCents > 0) {
        return {
            headerBackgroundColor: '#E74C3C', // red
            headerTextColor: 'white'
        };
    }

    // 3) Fully paid
    return {
        headerBackgroundColor: '#28a745', // green
        headerTextColor: 'white'
    };
};


export const isOrderPartiallyCollected = (order: {
    products?: Array<IOrderProduct | string>;
    buyAndGetProducts?: Array<IOrderProduct | string>;
}): boolean => {
    const allLines = [
        ...(order.products || []),
        ...(order.buyAndGetProducts || [])
    ].filter((line): line is IOrderProduct => typeof line !== 'string');

    const relevant = allLines.filter(line => line.status !== 'cancelled');
    if (relevant.length === 0) return false;

    const collectedCount = relevant.filter(line => line.status === 'collected').length;
    return collectedCount > 0 && collectedCount < relevant.length;
};

const getNonEmptyInvoiceId = (value?: string | null): string | undefined => {
    if (typeof value !== 'string') return undefined;

    const trimmedValue = value.trim();
    return trimmedValue || undefined;
};

type OrderInvoiceSearchPaymentLine =
    | Pick<Partial<IOrderPaymentLines>, 'facturapiPaymentInvoiceID' | 'facturapiInvoiceID'>
    | string
    | null
    | undefined;

export const getOrderInvoiceSearchId = (order?: {
    facturapiInvoiceID?: string | null;
    paymentLines?: OrderInvoiceSearchPaymentLine[] | null;
}): string | undefined => {
    const orderInvoiceId = getNonEmptyInvoiceId(order?.facturapiInvoiceID);
    if (orderInvoiceId) return orderInvoiceId;

    const paymentLines = Array.isArray(order?.paymentLines) ? order?.paymentLines : [];
    const paymentInvoiceLine = paymentLines.find((paymentLine) => {
        if (!paymentLine || typeof paymentLine === 'string') return false;
        return getNonEmptyInvoiceId(paymentLine.facturapiPaymentInvoiceID);
    });
    if (paymentInvoiceLine && typeof paymentInvoiceLine !== 'string') {
        const paymentInvoiceId = getNonEmptyInvoiceId(paymentInvoiceLine.facturapiPaymentInvoiceID);
        if (paymentInvoiceId) return paymentInvoiceId;
    }

    const invoiceLine = paymentLines.find((paymentLine) => {
        if (!paymentLine || typeof paymentLine === 'string') return false;
        return getNonEmptyInvoiceId(paymentLine.facturapiInvoiceID);
    });
    if (invoiceLine && typeof invoiceLine !== 'string') {
        return getNonEmptyInvoiceId(invoiceLine.facturapiInvoiceID);
    }

    return undefined;
};

export const buildOrderFolio = (prefix: string | null | undefined = DEFAULT_FOLIO_PREFIX, sequence: string | number | null | undefined) => {
    const effectivePrefix = (prefix || DEFAULT_FOLIO_PREFIX)?.toUpperCase();
    return `${effectivePrefix}-${sequence}`;
};
